import request from './request'
import { useUserStore } from '@/stores/user'
import { WEB_CLIENT_TYPE, getWebDeviceId } from '@/utils/auth-session'

// ==================== 知识空间（/api/ai/spaces） ====================

/** 我可见的空间列表（owner + ACL 可见） */
export function getSpaceList() {
  return request.get('/ai/spaces')
}

/** 创建空间 */
export function createSpace(data) {
  return request.post('/ai/spaces', data)
}

/** 重命名空间 */
export function renameSpace(spaceId, data) {
  return request.post(`/ai/spaces/${spaceId}/rename`, data)
}

/** 删除空间 */
export function deleteSpace(spaceId) {
  return request.delete(`/ai/spaces/${spaceId}`)
}

/** 共享授权 {subjectType, subjectId, level}（后端 ShareRequest record camelCase，直接透传） */
export function shareSpace(spaceId, data) {
  return request.post(`/ai/spaces/${spaceId}/acl`, data)
}

/** 既有授权列表 */
export function getSpaceAcl(spaceId) {
  return request.get(`/ai/spaces/${spaceId}/acl`)
}

/** 取消共享 {subjectType, subjectId, level}（同 shareSpace，camelCase 透传） */
export function unshareSpace(spaceId, data) {
  return request.post(`/ai/spaces/${spaceId}/acl/delete`, data)
}

/**
 * 空间文件列表（含处理状态）。
 * DTO 字段：id、attachmentId（URL 行为 null）、fileName、processStatus（PROCESSING/READY/FAILED）、
 * error（FAILED 原因，可空）、chunkCount（READY 分段数；CRM 未升级或处理未完成时为 null，消费侧需判空展示 -）、
 * sourceType（'FILE'|'URL'，契约 v1.3）、url（URL 行原文链接，v1.3）。
 */
export function getSpaceFiles(spaceId) {
  return request.get(`/ai/spaces/${spaceId}/files`)
}

/** 空间文件登记（fileKey → RAG 处理） */
export function uploadSpaceFile(spaceId, data) {
  return request.post(`/ai/spaces/${spaceId}/files`, data)
}

/** 删除空间文件 */
export function deleteSpaceFile(spaceId, attachmentId) {
  return request.delete(`/ai/spaces/${spaceId}/files/${attachmentId}`)
}

/**
 * 文件 AI 摘要（契约 v1.4 §14）：params={attachmentId}（FILE 行）或 {urlFileId}（URL 行）。
 * 返回 {summary(markdown), truncated, warnings}；LLM 生成 10-30s，调用方需 loading 态。
 */
export function summarizeSpaceFile(spaceId, params) {
  return request.post(`/ai/spaces/${spaceId}/files/summary`, null, { params })
}

/** FAILED 文件手动重推（后端复用推送网关，push_count 不清零） */
export function retrySpaceFile(spaceId, attachmentId) {
  return request.post(`/ai/spaces/${spaceId}/files/${attachmentId}/retry`)
}

/**
 * 空间附件下载（blob；契约 v1.2 §12，替代 kb policy 附件端点的临时借用）。
 * CRM 侧 VIEW 校验 + IDOR 防护；下载与 PDF 预览统一走本端点。
 */
export function downloadSpaceFile(spaceId, attachmentId) {
  return request.get(`/ai/spaces/${spaceId}/files/${attachmentId}/download`, { responseType: 'blob' })
}

/**
 * 空间搜索（契约 v1.3 §9；召回测试/全局搜索/问答溯源共用）。
 * @param {object} payload {spaceIds: []（空=全部可见空间）, query, topK?, mode?: 'vector'|'hybrid',
 *   filters?: {uploader?, filename_like?}}
 *   filters（v1.3 起生效，均可空，空值不传）：uploader=metadata 等值；filename_like=文件名子串（大小写不敏感）。
 *   被禁用分段（§13 enabled=false）的命中由 agent 侧一律排除，前端无需处理。
 * @returns {Promise<{hits: Array, mode: string, query_cost: object}>} hits 字段为 agent snake_case
 *   （doc_id/text/score/rerank_score?/space_id/space_name/filename/attachment_id/chunk_no/char_start/char_end）
 */
export function searchSpaces(payload) {
  return request.post('/ai/spaces/search', payload)
}

/**
 * URL 网页导入（契约 v1.3 §6.1；CRM 落行 source_type=URL/PROCESSING 后推送 agent url_import）。
 * 同空间同 URL 重复导入由 CRM 拒绝，业务异常 message 经拦截器/调用方提示。
 * @param {number|string} spaceId
 * @param {object} data {url（仅 http/https）, title?（可选，缺省取网页标题）}
 */
export function importSpaceUrl(spaceId, data) {
  return request.post(`/ai/spaces/${spaceId}/url-imports`, data)
}

/**
 * URL 导入行 FAILED 手动重推（重推 url_import）。
 * ⚠️ 契约 v1.3 未冻结该端点：前端按 url-imports 资源命名（file_id = ai_space_file.id），
 * 若 CRM 实现签名不同仅需调整本行（疑点已列入 W8 报告）。
 */
export function retrySpaceUrlImport(spaceId, fileId) {
  return request.post(`/ai/spaces/${spaceId}/url-imports/${fileId}/retry`)
}

/**
 * 删除 URL 导入行（agent 前缀删块 delete_url_file；端点说明同 retrySpaceUrlImport）。
 */
export function deleteSpaceUrlFile(spaceId, fileId) {
  return request.delete(`/ai/spaces/${spaceId}/url-imports/${fileId}`)
}

/**
 * 分段列表（契约 v1.3 §13；CRM 代理 VIEW 校验）。
 * @param {number|string} spaceId
 * @param {object} params 文件行传 {attachmentId}；URL 行传 {urlFileId}（= ai_space_file.id）
 * @returns {Promise<{chunks: Array<{doc_id, chunk_no, char_start, char_end, text, enabled}>}>} chunk_no 升序
 */
export function getSpaceFileChunks(spaceId, params) {
  return request.get(`/ai/spaces/${spaceId}/files/chunks`, { params })
}

/**
 * 分段启停（契约 v1.3 §13；MANAGE，CRM 校验 docId 归属空间后透传 agent metadata patch）。
 * @param {number|string} spaceId
 * @param {object} data {docId, enabled}
 */
export function toggleSpaceChunk(spaceId, data) {
  return request.post(`/ai/spaces/${spaceId}/files/chunks/toggle`, data)
}

/**
 * 空间问答 SSE（契约 v1.2 §11；POST /ai/spaces/qa，CRM SseRelay 逐行透传 agent 事件）。
 * 事件序列：refs → delta… → done → end；失败：error → end；空命中由 agent 发固定文案 delta。
 * SSE 无法走 axios，参照 agentSse.js/aiChat.js 约定用原生 fetch + AbortController。
 * @param {object} payload {spaceIds, query, conversationId?, history?: [{role, content}]}
 * @param {object} handlers {onRefs(refs), onDelta(text), onDone(data), onError(err), onEnd()}
 *   onEnd 在流正常终结（end/错误后/中断）时恰好触发一次；abort() 主动取消不触发。
 * @returns {{abort: () => void}} 取消句柄
 */
export function qaSpacesStream(payload, { onRefs, onDelta, onDone, onError, onEnd } = {}) {
  const userStore = useUserStore()
  const baseURL = process.env.VUE_APP_API_BASE_URL || ''
  const url = `${baseURL}/api/ai/spaces/qa`
  const controller = new AbortController()
  const token = userStore.accessToken ? `${userStore.tokenType} ${userStore.accessToken}` : ''
  let active = true
  let ended = false

  const fail = (err) => { if (active && onError) onError(err) }
  /** 终结去重：end 事件/错误/流中断只走一次 onEnd（abort 除外） */
  const finish = () => {
    if (ended || !active) return
    ended = true
    active = false
    if (onEnd) onEnd()
  }

  ;(async () => {
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
          'X-Device-Id': getWebDeviceId(),
          'X-Client-Type': WEB_CLIENT_TYPE,
          Accept: 'text/event-stream'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      })
      if (!resp.ok) {
        let msg = `问答流打开失败: ${resp.status}`
        try {
          const body = await resp.json()
          msg = body.message || body.msg || msg
        } catch { /* 非 JSON 错误体，用状态码文案 */ }
        fail(new Error(msg))
        finish()
        return
      }
      const reader = resp.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      let eventName = ''
      let dataLines = []

      const dispatch = () => {
        if (dataLines.length === 0) return
        const raw = dataLines.join('\n')
        const name = eventName || 'message'
        eventName = ''
        dataLines = []
        if (!active) return
        let data = raw
        try { data = JSON.parse(raw) } catch { /* 非 JSON 原文透传 */ }
        switch (name) {
          case 'refs':
            if (onRefs) onRefs((data && data.refs) || [])
            break
          case 'delta':
            if (onDelta) onDelta((data && data.text) || '')
            break
          case 'done':
            if (onDone) onDone(data || {})
            break
          case 'error':
            fail(new Error((data && data.error) || '空间问答失败'))
            break
          case 'end':
            finish()
            break
          default:
            break  // 心跳等未知事件忽略
        }
      }

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (line.startsWith(':')) continue             // 心跳/注释行（SseRelay 15s）
          if (line.startsWith('event:')) { eventName = line.slice(6).trim(); continue }
          if (line.startsWith('data:')) { dataLines.push(line.slice(5).trim()); continue }
          if (line.trim() === '') dispatch()            // 空行 = 事件边界
        }
      }
      dispatch()
      if (active && !ended) fail(new Error('问答流中断（未收到 end）'))
      finish()
    } catch (err) {
      if (err && err.name === 'AbortError') return
      fail(err)
      finish()
    }
  })()

  return {
    abort() {
      active = false
      ended = true
      controller.abort()
    }
  }
}
